import { observer } from 'mobx-react';
import { Button, Input, InputType, InputDropdown, InputCalendar, InputOptionBox } from '../../../../core/components/Index';
import ApplyViewModel from './ApplyViewModel';
import Helper, { formatDate } from '../../../../core/services/Helper';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { Plus } from 'lucide-react';
import { BeneficiaryPaymentOption, BeneficiaryRelation } from '../OraEnums';
import CountryList from '../../../../assets/data/Country.json';
import { useEffect, useRef, useState } from 'react';

const BeneficiaryBenefit = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  const errorRef = useRef<HTMLDivElement>(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.BeneficiaryBenefit, memberId!);
  }, []);

  useEffect(() => {
    if (vm.beneficiaryCurrentState?.validationMessage !== "" && errorRef.current) {
      errorRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [vm.beneficiaryCurrentState?.validationMessage, submitAttempt]);

  const handleSubmit = async (submitFunction: () => Promise<boolean>) => {
    setSubmitAttempt(prev => prev + 1);
    await submitFunction();
  };

  return (
    <>
      {
        vm.beneficiaryCurrentState!.validationMessage !== "" &&
        <>
          <div ref={errorRef} className="gap-4 p-6 border-[2px] border-[#D82042] bg-[#d8204212] flex rounded-md mb-4" >
            <div><svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
              <path d="M12.5 25.5C15.8152 25.5 18.9946 24.183 21.3388 21.8388C23.683 19.4946 25 16.3152 25 13C25 9.68479 23.683 6.50537 21.3388 4.16117C18.9946 1.81696 15.8152 0.5 12.5 0.5C9.18479 0.5 6.00537 1.81696 3.66117 4.16117C1.31696 6.50537 0 9.68479 0 13C0 16.3152 1.31696 19.4946 3.66117 21.8388C6.00537 24.183 9.18479 25.5 12.5 25.5ZM12.5 6.75C13.1494 6.75 13.6719 7.27246 13.6719 7.92188V13.3906C13.6719 14.04 13.1494 14.5625 12.5 14.5625C11.8506 14.5625 11.3281 14.04 11.3281 13.3906V7.92188C11.3281 7.27246 11.8506 6.75 12.5 6.75ZM10.9375 17.6875C10.9375 17.2731 11.1021 16.8757 11.3951 16.5826C11.6882 16.2896 12.0856 16.125 12.5 16.125C12.9144 16.125 13.3118 16.2896 13.6049 16.5826C13.8979 16.8757 14.0625 17.2731 14.0625 17.6875C14.0625 18.1019 13.8979 18.4993 13.6049 18.7924C13.3118 19.0854 12.9144 19.25 12.5 19.25C12.0856 19.25 11.6882 19.0854 11.3951 18.7924C11.1021 18.4993 10.9375 18.1019 10.9375 17.6875Z" fill="#D82042" />
            </svg>
            </div>
            <div className='-mt-1'>
              <div className='text-[#D82042] text-22px font-semibold mb-1 tracking--1percent'>{vm.beneficiaryCurrentState?.validationMessageTitle}</div>
              <p className="text-black text-15px tracking--1_5percent  ">
                {Helper.formattedHtml(vm.beneficiaryCurrentState!.validationMessage)}
              </p>
            </div>
          </div>
        </>
      }
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 id="page-title"  className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Beneficiary Designation</h1>
          {vm.beneficiaryCurrentState!.memberDeclarationVisible &&
            <p className="text-gray-700 font-medium text-22px">{(vm.model.beneficiaryDesignation.beneDesigMinorChild === "N" && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0) ? 'Retirement Benefit - Return of Contributions - Summary' : 'Retirement Benefit Summary'}</p>
          }
          {!vm.beneficiaryCurrentState!.memberDeclarationVisible &&
            <>
              <p className="text-gray-700 font-medium text-22px">Retirement Benefit {(vm.model.beneficiaryDesignation.beneDesigMinorChild === "N" && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0) ? '- Return of Contributions' : ''}</p>
              {
                vm.model.beneficiaryDesignation?.beneficiaries?.length! === 0 &&
                <p className="text-black text-15px tracking--1percent leading-26px">
                  {Helper.formattedHtml(vm.beneficiaryCurrentState?.pageTitle!)}
                </p>
              }
              {!(vm.model.beneficiaryDesignation.beneDesigMinorChild === 'Y'
                || vm.model.beneficiaryDesignation.beneDesigMinorChild === 'N')
                && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
                <p className="text-black text-15px tracking--1percent leading-26px">
                  {Helper.formattedHtml(vm.beneficiaryCurrentState?.pageTitle!)}
                </p>
              }

              {vm.model.beneficiaryDesignation.beneDesigMinorChild === "Y"
                && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
                <>
                  <p className="text-black text-15px tracking--1percent leading-26px">
                    You have indicated that you wish to designate eligible minor child(ren) as a beneficiary for refundable contributions. Please enter their information below.
                  </p>
                </>
              }
              {vm.model.beneficiaryDesignation.beneDesigMinorChild === "N"
                && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
                <>
                  <p className="text-black text-15px tracking--1percent leading-26px">
                    Since you indicated you do not have any eligible minor child(ren) to receive a monthly continuance, you may designate a beneficiary to receive any refundable member contributions held by SCERS at the time of your death.
                  </p>
                </>
              }
              {vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
                !(vm.model.beneficiaryDesignation.beneDesigMinorChild === 'Y'
                  || vm.model.beneficiaryDesignation.beneDesigMinorChild === 'N') &&
                <div className="mt-4 mb-4">
                  {!vm.beneficiaryCurrentState?.readMoreVisible && <button onClick={async () => await vm.setBeneficiaryReadMoreVisible(true)} className="text-primary-500 text-sm underline">
                    Read More [+]
                  </button>}

                  {vm.beneficiaryCurrentState?.readMoreVisible && <button onClick={async () => await vm.setBeneficiaryReadMoreVisible(false)} className="text-primary-500 text-sm underline">
                    Read Less [-]
                  </button>
                  }
                </div>
              }
              {vm.beneficiaryCurrentState?.readMoreVisible && <p className='mt-4 mb-4'>
                <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px">
                  {Helper.formattedHtml(vm.beneficiaryCurrentState?.pageOption!)}
                </ul>
              </p>
              }

              {vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
                <h4 className="text-gray-700 font-medium text-22px mt-4 mb-4">Beneficiary Information</h4>
              }
              {vm.beneficiaryCurrentState!.addNewTopButtonVisible &&
                <button onClick={async () => await vm.addBeneficiaryAsync()}
                  className="hover:bg-primary-50/20 cursor-pointer w-full flex flex-col items-center justify-center  rounded-lg p-5 text-center"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23D0CDC8' stroke-width='6' stroke-dasharray='0%2c 12' stroke-dashoffset='12' stroke-linecap='square'/%3e%3c/svg%3e\")",
                    borderRadius: "6px"
                  }}>
                  <p className="text-primary-500 text-22px font-semibold">{vm.beneficiaryCurrentState!.addNewButtonText}</p>
                  <div className="mt-2 bg-primary-500 text-white rounded-full w-10 h-10 flex items-center justify-center border border-primary-600">
                    <Plus className='w-8 h-8' />
                  </div>
                </button>
              }
              {vm.beneficiaryCurrentState?.showDesignateMinorChildOptions &&
                <>
                  <div className="space-y-2">
                    <p className="text-black text-15px tracking--1percent leading-26px font-semibold">Do you have eligible minor child(ren) you wish to designate to receive a monthly continuance?</p>
                    <div className="flex items-start pl-6">
                      <InputOptionBox
                        inputId="beneDesigMinorChildYes"
                        value={vm.model.beneficiaryDesignation.beneDesigMinorChild}
                        checked={vm.model.beneficiaryDesignation.beneDesigMinorChild && vm.model.beneficiaryDesignation.beneDesigMinorChild === 'Y' ? true : false}
                        onChange={(e: any) => e.checked ? vm.model.beneficiaryDesignation.beneDesigMinorChild = "Y" : vm.model.beneficiaryDesignation.beneDesigMinorChild = undefined}
                        className="mt-2"
                      />
                      <label htmlFor="beneDesigMinorChildYes" className="ml-2 text-black text-15px tracking--1percent leading-26px cursor-pointer mt-2">
                        Yes, I wish to designate minor child(ren) as continuance beneficiaries.
                      </label>
                    </div>
                    <div className="flex items-start pl-6">
                      <InputOptionBox
                        inputId="beneDesigMinorChildNo"
                        value={vm.model.beneficiaryDesignation.beneDesigMinorChild}
                        checked={vm.model.beneficiaryDesignation.beneDesigMinorChild && vm.model.beneficiaryDesignation.beneDesigMinorChild === 'N' ? true : false}
                        onChange={(e: any) => e.checked ? vm.model.beneficiaryDesignation.beneDesigMinorChild = "N" : vm.model.beneficiaryDesignation.beneDesigMinorChild = undefined}
                        className="mt-[1px]"
                      />
                      <label htmlFor="beneDesigMinorChildNo" className="ml-2 text-black text-15px tracking--1percent leading-26px cursor-pointer">
                        {vm.beneficiaryCurrentState!.NoEligibleBeneMessage}
                      </label>
                    </div>
                  </div>
                </>
              }
            </>}
        </div>

        {vm.beneficiaryCurrentState!.isAddingBeneficiary || vm.beneficiaryCurrentState!.isEditingBeneficiary ? (

          <div className="space-y-6 max-w-lg">
            <div className="flex sm:space-x-6 space-y-4 sm:space-y-0 flex-col sm:flex-row">
              <div className="md:w-1/2 w-full">
                <InputDropdown
                  id="lovBeneRelationshipId"
                  value={vm.beneficiaryModel?.lovBeneRelationshipId}
                  options={vm.beneficiaryCurrentState?.relationships}
                  optionValue="key"
                  optionLabel="value"
                  label="Relationship"
                  placeholder="Select"
                  onChange={(e) => {
                    vm.onInputChange(e, 'lovBeneRelationshipId', vm.beneficiaryModel);
                    if (vm.beneficiaryCurrentState?.isMarriedOrRegisteredPartnership) {
                      if (vm.beneficiaryModel.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild &&
                        vm.model.optionSelection.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance
                      ) {
                        Appl.MessageBox.show(`Since you've identified your marital status as <b>Married</b>, SCERS is required to notify your spouse or registered domestic partner if you make changes to your beneficiary, request a refund of your contributions, or choose an alternative option for retirement benefits. In most cases, your spouse or partner must approve the designation of a different beneficiary.<br/><br/>You may proceed with this designation; however, SCERS will need acknowledgment from your spouse or registered domestic partner. This can be provided either through a notarized document or by signing in person at the SCERS office. After your application is submitted and assigned, we will contact you with further instructions.`, `Important Beneficiary Information`, ``, `I Understand`, true);
                      }
                    }
                  }}
                  className="w-full mt-1 flex-1 md:!w-full"
                  required={false}
                  readOnly={vm.beneficiaryCurrentState?.readOnlyRelationship}
                />
              </div>
              {vm.beneficiaryCurrentState!.manuallyProvidePercentage &&
                <div className="md:w-1/2 min-w-[60px]">
                  <div className="flex items-center">
                    <div className="h-full group">
                      <Input
                        id="percentage"
                        value={vm.beneficiaryModel?.percentage}
                        label="Benefit Percentage"
                        inputType={InputType.Number}
                        onChange={(e) => vm.onInputChange(e, 'percentage', vm.beneficiaryModel)}
                        placeholder="Benefit Percentage"
                        maxLength={5}
                        required={false}
                        prefix="%"
                        prefixPosition="right" />
                    </div>
                  </div>
                </div>
              }
            </div>
            {vm.beneficiaryModel?.lovBeneRelationshipId &&
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6 '>
                  {vm.beneficiaryModel?.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (<>
                    <div>
                      <Input
                        id="beneFirstName"
                        value={vm.beneficiaryModel?.beneFirstName}
                        inputType={InputType.Text}
                        label="First Name"
                        onChange={(e) => vm.onInputChange(e, 'beneFirstName', vm.beneficiaryModel)}
                        placeholder="First Name"
                        required={false}
                      />
                    </div>
                    <div>
                      <Input
                        id="beneLastName"
                        value={vm.beneficiaryModel?.beneLastName}
                        inputType={InputType.Text}
                        label="Last Name"
                        onChange={(e) => vm.onInputChange(e, 'beneLastName', vm.beneficiaryModel)}
                        placeholder="Last Name"
                        required={false}
                      />
                    </div>
                    <div>
                      <InputCalendar
                        id="beneBirthDate"
                        value={vm.beneficiaryModel?.beneBirthDate!}
                        label="Date of Birth"
                        showHint={true}
                        onChange={(e) => {
                          vm.onInputChange(e, 'beneBirthDate', vm.beneficiaryModel);
                        }}
                        required={false}
                        maxDate={new Date()}
                      />
                    </div>
                    <div>
                      {vm.beneficiaryModel?.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                        <label className="font-bold text-15px text-black mb-1 block" htmlFor="beneSsn">
                          Social Security Number <span className="block font-light block text-[13px] font-normal text-gray-900">9 digits, no dashes or other characters</span>
                        </label>
                      )}
                      <Input
                        id="beneSsn"
                        value={vm.beneficiaryModel?.beneSsn}
                        inputType={InputType.Secret}
                        onChange={(e) => vm.onInputChange(e, 'beneSsn', vm.beneficiaryModel)}
                        placeholder=""
                        maxLength={9}
                        style={{ width: '800px' }}
                        required={false}
                      />
                    </div>
                  </>
                  )}
                  {vm.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg && (<>
                    <div className='col-span-2'>
                      <Input
                        id="beneOtherName"
                        value={vm.beneficiaryModel?.beneOtherName}
                        inputType={InputType.Text}
                        label="Entity Name"
                        onChange={(e) => vm.onInputChange(e, 'beneOtherName', vm.beneficiaryModel)}
                        style={{ width: '500px' }}
                        required={false}
                      />
                    </div>

                    <div>
                      <label className="font-semibold mb-2 block" htmlFor="beneTin">
                        Tax Identification Number <span className="block font-light block text-[13px] font-normal text-gray-900">9 digits, no dashes or other characters</span>
                      </label>
                      <Input
                        id="beneTin"
                        value={vm.beneficiaryModel?.beneTin}
                        inputType={InputType.Secret}
                        onChange={(e) => vm.onInputChange(e, 'beneTin', vm.beneficiaryModel)}
                        placeholder=""
                        maxLength={9}
                        style={{ width: '500px' }}
                        required={false}
                      />
                    </div>
                    <div></div>
                  </>)}

                  <div className='md:col-span-2'>
                    <h4 className="text-gray-700 font-medium text-22px mb-4">Beneficiary Address</h4>
                    <div>
                      <InputDropdown
                        id="beneCountry"
                        value={vm.beneficiaryModel?.beneCountry}
                        options={CountryList}
                        optionValue="key"
                        optionLabel="key"
                        label="Country"
                        placeholder='Select Country'
                        onChange={(e) => vm.onInputChange(e, 'beneCountry', vm.beneficiaryModel)}
                      className='!w-full md:max-w-[600px]'
                        required={false}
                      />
                    </div>
                    <div className="mt-2 mb-4">
                      <Input
                        id="beneStreetAddress"
                        value={vm.beneficiaryModel?.beneStreetAddress}
                        inputType={InputType.Text}
                        label="Address Line 1"
                        onChange={(e) => {
                          vm.onInputChange(e, 'beneStreetAddress', vm.beneficiaryModel);
                        }}
                        required={false}
                        //style={{ width: '600px' }}
                      />
                    </div>
                    <div className="mt-2 mb-4">
                      <Input
                        id="beneStreetAddress2"
                        value={vm.beneficiaryModel?.beneStreetAddress2}
                        inputType={InputType.Text}
                        label="Address Line 2"
                        onChange={(e) => {
                          vm.onInputChange(e, 'beneStreetAddress2', vm.beneficiaryModel);
                        }}
                        required={false}
                     //   style={{ width: '600px' }}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full md:max-w-[600px]">
                      <div>
                        <Input
                          id="beneCity"
                          value={vm.beneficiaryModel?.beneCity}
                          inputType={InputType.Text}
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneCity', vm.beneficiaryModel);
                          }}
                          label="City"
                          required={false}

                        />
                      </div>
                      <div>
                        <Input
                          id="beneState"
                          value={vm.beneficiaryModel?.beneState}
                          inputType={InputType.Text}
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneState', vm.beneficiaryModel);
                          }}
                          label={vm.beneficiaryModel?.beneCountry === 'United States' ? 'State' : 'State/Province/Region'}
                          required={false}
                          
                        />
                      </div>
                      <div>
                        <Input
                          id="beneZip"
                          value={vm.beneficiaryModel?.beneZip}
                          inputType={InputType.Text}
                          onChange={(e) => {
                            vm.onInputChange(e, 'beneZip', vm.beneficiaryModel);
                          }}
                          label={vm.beneficiaryModel?.beneCountry === 'United States' ? 'Zip' : 'Zip/Postal Code'}
                          maxLength={vm.beneficiaryModel?.beneCountry === 'United States' ? 5 : 10}
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>}
            <div className="flex space-x-4 mt-6">
              {
                vm.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg ?
                  <Button title="Save & Continue" rounded onClick={async () => await vm.createOrUpdateBeneficiaryAsync()}
                    disabled={
                      vm.beneficiaryModel?.lovBeneRelationshipId === undefined
                      || vm.beneficiaryModel?.beneOtherName === ""
                      || vm.beneficiaryModel?.beneCountry === ""
                      || vm.beneficiaryModel?.beneStreetAddress === ""
                      || vm.beneficiaryModel?.beneCity === ""
                      || vm.beneficiaryModel?.beneState === ""
                      || vm.beneficiaryModel?.beneZip === ""
                    }
                  />
                  :
                  <Button title="Save & Continue" rounded onClick={async () => await vm.createOrUpdateBeneficiaryAsync()}
                    disabled={
                      vm.beneficiaryModel?.lovBeneRelationshipId === undefined
                      || vm.beneficiaryModel?.beneFirstName === ""
                      || vm.beneficiaryModel?.beneLastName === ""
                      || vm.beneficiaryModel?.beneBirthDate === ""
                      || vm.beneficiaryModel?.beneSsn === ""
                      || vm.beneficiaryModel?.beneCountry === ""
                      || vm.beneficiaryModel?.beneStreetAddress === ""
                      || vm.beneficiaryModel?.beneCity === ""
                      || vm.beneficiaryModel?.beneState === ""
                      || vm.beneficiaryModel?.beneZip === ""
                    }
                  />
              }
              <Button title="Cancel" severity='warning' outlined rounded onClick={async () => await vm.cancelAddBeneficiaryAsync()} />
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {vm.model.beneficiaryDesignation?.beneficiaries?.map((beneficiary, index) => (
                <div key={index} className="p-6 bg-cream-50 rounded-lg flex flex-col justify-between">
                  <h4 className="text-22px mb-4 text-primary-500 font-semibold">{vm.model.beneficiaryDesignation?.beneficiaries?.length === 1 ? 'Beneficiary' : `Beneficiary ${index + 1}`}</h4>
                  <div className='flex flex-col flex-1'>
                    {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                      <div className="text-gray-700 py-1.5 flex items-start justify-between">
                        <dt className="text-15px font-normal leading-6 ">Name</dt>
                        <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                          {beneficiary.beneFirstName} {beneficiary.beneLastName}
                        </dd>
                      </div>)}
                    {beneficiary.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg && (
                      <div className="text-gray-700 py-1.5 flex items-start justify-between">
                        <dt className="text-15px font-normal leading-6 ">Entity Name</dt>
                        <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                          {beneficiary.beneOtherName}
                        </dd>
                      </div>)}
                    <div className="text-gray-700 py-1.5 flex items-start justify-between">
                      <dt className="text-15px font-normal leading-6 ">Relationship</dt>
                      <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{vm.model.beneficiaryRelationListItems?.find(o => o.key === beneficiary.lovBeneRelationshipId)?.value} </dd>
                    </div>
                    {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg && (
                      <div className="text-gray-700 py-1.5 flex items-start justify-between">
                        <dt className="text-15px font-normal leading-6 ">Date of Birth</dt>
                        <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{formatDate(beneficiary.beneBirthDate)} </dd>
                      </div>
                    )}
                    {beneficiary.lovBeneRelationshipId !== BeneficiaryRelation.EntityTrustOrg &&
                      <div className="text-gray-700 py-1.5 flex items-start justify-between">
                        <dt className="text-15px font-normal leading-6 ">SSN</dt>
                        <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">XXX-XX-{beneficiary.beneSsn?.substring(5, 9)}</dd>
                      </div>
                    }
                    {beneficiary.lovBeneRelationshipId === BeneficiaryRelation.EntityTrustOrg &&
                      <>
                        <div className="text-gray-700 py-1.5 flex items-start justify-between">
                          &nbsp;
                        </div>
                        <div className="text-gray-700 py-1.5 flex items-start justify-between">
                          <dt className="text-15px font-normal leading-6 ">TIN</dt>
                          <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">{beneficiary.beneTin!}</dd>
                        </div>
                      </>
                    }

                    <div className="text-gray-700 py-1.5 flex items-start justify-between">
                      <dt className="text-15px font-normal leading-6">Address</dt>
                      <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-0 text-right">
                        {Helper.getFormattedAddress(beneficiary.beneStreetAddress, beneficiary.beneStreetAddress2, beneficiary.beneCity, beneficiary.beneState, beneficiary.beneZip, beneficiary.beneCountry)}
                      </dd>
                    </div>
                    <div className='flex-1' />
                    {vm.beneficiaryCurrentState!.manuallyProvidePercentage &&
                      <div className="text-gray-700 py-1.5 flex items-start justify-between border-t mt-2 items-center">
                        <dt className="text-15px font-normal leading-6 sm:mt-2 font-semibold">Percentage of Allowance</dt>
                        <dd className="mt-1 text-15px leading-6 sm:col-span-2 sm:mt-2 text-right font-semibold">{beneficiary.percentage}%</dd>
                      </div>
                    }
                  </div>
                  {!vm.beneficiaryCurrentState!.memberDeclarationVisible && <div className="pt-6 flex items-center justify-center border-t mt-2">
                    <Button className="mr-2" title="Edit" rounded onClick={async () => await vm.editBeneficiaryAsync(index)} />
                    <Button title="Delete" severity='warning' outlined rounded style={{ backgroundColor: 'white', color: 'rgb(207 113 25)' }} onClick={() => Appl.ConfirmBox.show('If you proceed, this beneficiary will be permanently removed from your retirement application, and you will need to re-enter all their information if you wish to add them again later.', 'Are You Sure You Wish to Delete?', '', "Yes, I am sure", 'Cancel', async () => await vm.removeBeneficiaryAsync(index), undefined, true)} />
                  </div>
                  }
                </div>
              ))}
              {vm.beneficiaryCurrentState?.addNewSideButtonVisible &&
                <button onClick={async () => await vm.addBeneficiaryAsync()}
                  className="hover:bg-primary-50/20 cursor-pointer w-full flex flex-col items-center justify-center  rounded-lg p-5 text-center"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23D0CDC8' stroke-width='6' stroke-dasharray='0%2c 12' stroke-dashoffset='12' stroke-linecap='square'/%3e%3c/svg%3e\")",
                    borderRadius: "6px"
                  }}>
                  <p className="text-primary-500 text-22px font-semibold">{vm.beneficiaryCurrentState!.addNewButtonText}</p>
                  <div className="mt-2 bg-primary-500 text-white rounded-full w-10 h-10 flex items-center justify-center border border-primary-600">
                    <Plus className='w-8 h-8' />
                  </div>
                </button>
              }
            </div>
            {vm.model.beneficiaryDesignation?.beneficiaries?.length! >= 1
              && vm.beneficiaryCurrentState?.isMarriedOrRegisteredPartnership
              && vm.model.optionSelection?.beneficiaryPaymentOptionId === BeneficiaryPaymentOption.UnmodifiedAllowance
              && vm.beneficiaryModel?.lovBeneRelationshipId === BeneficiaryRelation.MinorDependentChild && <p>
                Please note: If multiple beneficiaries are designated, the Unmodified Allowance continuance amount will be divided equally among them.
              </p>
            }
            {vm.model.beneficiaryDesignation.beneDesigMinorChild === "N" && vm.model.beneficiaryDesignation?.beneficiaries?.length! > 0 &&
              <p>
                Please note: If multiple beneficiaries are designated, the Unmodified Allowance continuance amount will be divided equally among them.
              </p>
            }
            {vm.beneficiaryCurrentState!.memberDeclarationVisible &&
              <>
                <p className="text-gray-700 font-medium text-22px">Member Declaration</p>
                <p>Section 31760.3 of the Government Code requires the Sacramento County Employees' Retirement System (hereinafter "Plan") to notify your current spouse or registered domestic partner if you change your beneficiary, request a refund of accumulated contributions, or elect an optional settlement of retirement benefits. With limited exceptions, the Plan cannot allow the designation of an alternate beneficiary without the approval of the current spouse or registered domestic partner.</p>
                <p>
                  <div className="flex items-start space-x-2">
                    <InputOptionBox
                      inputId="afrDecleration"
                      value={vm.model.beneficiaryDesignation.afrDecleration}
                      checked={vm.model.beneficiaryDesignation.afrDecleration === "S"
                        || vm.model.beneficiaryDesignation.afrDecleration === "M"
                        || vm.model.beneficiaryDesignation.afrDecleration === "N"}
                      onChange={(e: any) => { e.target.checked ? vm.model.beneficiaryDesignation.afrDecleration = vm.beneficiaryCurrentState?.memberDeclarationValue! : vm.model.beneficiaryDesignation.afrDecleration = "" }}
                    />
                    <label htmlFor="afrDecleration" className="ml-2 text-base">
                      {vm.beneficiaryCurrentState?.memberDeclarationText}
                    </label>
                  </div>
                </p>
              </>
            }
            <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
              {!vm.beneficiaryCurrentState?.memberDeclarationVisible &&
                <>
                  {vm.beneficiaryCurrentState?.showDesignateMinorChildOptions &&
                    <Button
                      title="Save & Continue" rounded
                      onClick={async () => await vm.onSetDesignateMinorChildOptionsAsync()}
                      disabled={vm.model.beneficiaryDesignation.beneDesigMinorChild === undefined || vm.model.beneficiaryDesignation.beneDesigMinorChild === ''}
                    />
                  }
                  {!vm.beneficiaryCurrentState?.showDesignateMinorChildOptions &&
                    <Button
                      title="Save & Continue" rounded
                      onClick={async () => await handleSubmit(() => vm.onValidateBeneficiaryStepAsync())}
                      disabled={vm.model.beneficiaryDesignation.beneficiaries?.length === 0
                      }
                    />
                  }
                  {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
                    onClick={async () => await vm.navigateToReviewStepAsync()}
                  />}
                </>
              }
              {vm.beneficiaryCurrentState!.memberDeclarationVisible &&
                <>
                  <Button
                    title={`${vm.navigatedFromReviewScreen ? 'Save & Return to Review' : 'Save & Continue'}`}
                    rounded
                    onClick={async () => await handleSubmit(() => vm.onBeneficiaryStepCompleteAsync())}
                    disabled={vm.model.beneficiaryDesignation.afrDecleration === ""
                      || vm.model.beneficiaryDesignation.afrDecleration === undefined}
                  />
                  <Button
                    title="Edit Designation"
                    rounded
                    severity='warning' outlined
                    onClick={() => vm.beneficiaryCurrentState!.memberDeclarationVisible = false}
                  />
                </>
              }

            </div>
          </>
        )}
      </div>
    </>
  );
});

export default BeneficiaryBenefit;
